import * as Comlink from 'comlink';
import geobuf from 'geobuf';
import GtfsRealtimeBindings from 'gtfs-realtime-bindings';
import Pbf from 'pbf';
import configs, {buildApiRequestHeaders} from './configs';
import {loadJSON, removePrefix} from './helpers/helpers';
import {decode} from './helpers/helpers-gtfs';
export * from './loader/earthquake';
export * from './loader/tsunami';
export * from './loader/typhoon';

const RAILWAYS_FOR_TRAINS = {
    odpt: [
        'Toei.Asakusa',
        'Toei.Mita',
        'Toei.Shinjuku',
        'Toei.Oedo',
        'Toei.Arakawa'
    ],
    challenge2024: [
        'JR-East.Yamanote',
        'Keikyu.Main',
        'Keikyu.Airport',
        'Keikyu.Daishi',
        'Keikyu.Zushi',
        'Keikyu.Kurihama',
        'Tobu.TobuSkytree',
        'Tobu.TobuSkytreeBranch',
        'Tobu.Isesaki',
        'Tobu.Nikko',
        'Tobu.TobuUrbanPark',
        'Tobu.Kameido',
        'Tobu.Daishi',
        'Tobu.Tojo',
        'Tobu.Ogose'
    ]
};

const OPERATORS_FOR_TRAININFORMATION = {
    odpt: [
        'TWR',
        'TokyoMetro',
        'Toei',
        'YokohamaMunicipal',
        'MIR',
        'TamaMonorail'
    ],
    challenge2024: [
        'jre-is',
        'Tokyu',
        'Keikyu',
        'Tobu',
        'Seibu',
        'Keio'
    ]
};

const RAILWAY_SOBURAPID = 'JR-East.SobuRapid';

const TRAINTYPE_JREAST_LIMITEDEXPRESS = 'JR-East.LimitedExpress';

function getTimetableFileName(clock) {
    const calendar = clock.getCalendar() === 'Weekday' ? 'weekday' : 'holiday';

    return `timetable-${calendar}.json.gz`;
}

function getExtraTimetableFileNames(clock) {
    const calendar = clock.getCalendar();

    if (calendar === 'Saturday') {
        return ['timetable-saturday.json.gz'];
    }
    if (calendar === 'Holiday') {
        return ['timetable-sunday-holiday.json.gz'];
    }
    return [];
}

function adjustTrainID(id, type, destination) {
    if (type === TRAINTYPE_JREAST_LIMITEDEXPRESS &&
        destination[0].match(/NaritaAirportTerminal1|Takao|Ofuna|Omiya|Ikebukuro|Shinjuku/)) {
        return id.replace(/JR-East\.(NaritaAirportBranch|Narita|Sobu)/, RAILWAY_SOBURAPID);
    }
    return id;
}

/**
 * Load all the static data.
 * @param {string} dataUrl - Data URL
 * @param {string} lang - IETF language tag for dictionary
 * @param {Clock} clock - Clock object representing the current time
 * @returns {Object} Loaded data
 */
export function loadStaticData(dataUrl, lang, clock) {
    const extra = getExtraTimetableFileNames(clock);

    return Promise.all([
        `${dataUrl}/dictionary-${lang}.json`,
        `${dataUrl}/railways.json.gz`,
        `${dataUrl}/stations.json.gz`,
        `${dataUrl}/features.json.gz`,
        `${dataUrl}/${getTimetableFileName(clock)}`,
        `${dataUrl}/rail-directions.json.gz`,
        `${dataUrl}/train-types.json.gz`,
        `${dataUrl}/train-vehicles.json.gz`,
        `${dataUrl}/operators.json.gz`,
        `${dataUrl}/airports.json.gz`,
        `${dataUrl}/flight-statuses.json.gz`,
        `${dataUrl}/poi.json.gz`,
        ...extra.map(name => `${dataUrl}/${name}`)
    ].map(loadJSON)).then(data => ({
        dict: data[0],
        railwayData: data[1],
        stationData: data[2],
        featureCollection: data[3],
        timetableData: data[4].concat(...data.slice(12)),
        railDirectionData: data[5],
        trainTypeData: data[6],
        trainVehicleData: data[7],
        operatorData: data[8],
        airportData: data[9],
        flightStatusData: data[10],
        poiData: data[11]
    }));
}

/**
 * Load the timetable data.
 * @param {string} dataUrl - Data URL
 * @param {Clock} clock - Clock object representing the current time
 * @returns {Object} Loaded timetable data
 */
export function loadTimetableData(dataUrl, clock) {
    const extra = getExtraTimetableFileNames(clock);

    return Promise.all([
        `${dataUrl}/${getTimetableFileName(clock)}`,
        ...extra.map(name => `${dataUrl}/${name}`)
    ].map(loadJSON)).then(data => data[0].concat(...data.slice(1)));
}

/**
 * Load the dynamic data for trains.
 * @param {Object} secrets - Secrets object
 * @returns {Object} Loaded data
 */
export function loadDynamicTrainData(secrets) {
    const trainData = [],
        trainInfoData = [],
        urls = [];

    for (const source of Object.keys(RAILWAYS_FOR_TRAINS)) {
        const url = configs.apiUrl[source],
            key = secrets[source];

        if (source === 'odpt' || source === 'challenge2024') {
            const railways = RAILWAYS_FOR_TRAINS[source]
                .map((railway) => `odpt.Railway:${railway}`)
                .join(',');

            urls.push(`${url}odpt:Train?odpt:railway=${railways}&acl:consumerKey=${key}`);
        }
    }

    urls.push(configs.tidUrl);

    for (const source of Object.keys(OPERATORS_FOR_TRAININFORMATION)) {
        const url = configs.apiUrl[source],
            key = secrets[source];

        if (source === 'odpt' || source === 'challenge2024') {
            const operators = OPERATORS_FOR_TRAININFORMATION[source]
                .map(operator => `odpt.Operator:${operator}`)
                .join(',');

            urls.push(`${url}odpt:TrainInformation?odpt:operator=${operators}&acl:consumerKey=${key}`);
        }
    }

    urls.push(configs.trainInfoUrl);

    return Promise.all(urls.map(loadJSON)).then(data => {
        // Train data from ODPT and Challenge 2024
        for (const train of [...data.shift(), ...data.shift()]) {
            const trainType = removePrefix(train['odpt:trainType']),
                destinationStation = removePrefix(train['odpt:destinationStation']);

            trainData.push({
                id: adjustTrainID(removePrefix(train['owl:sameAs']), trainType, destinationStation),
                o: removePrefix(train['odpt:operator']),
                r: removePrefix(train['odpt:railway']),
                y: trainType,
                n: train['odpt:trainNumber'],
                os: removePrefix(train['odpt:originStation']),
                d: removePrefix(train['odpt:railDirection']),
                ds: destinationStation,
                ts: removePrefix(train['odpt:toStation']),
                fs: removePrefix(train['odpt:fromStation']),
                delay: (train['odpt:delay'] || 0) * 1000,
                carComposition: train['odpt:carComposition'],
                date: train['dc:date'].replace(/([\d\-])T([\d:]+).*/, '$1 $2')
            });
        }

        // Train data from others
        for (const train of data.shift()) {
            trainData.push(train);
        }

        // Train information data from ODPT and Challenge 2024
        for (const trainInfo of [...data.shift(), ...data.shift()]) {
            trainInfoData.push({
                operator: removePrefix(trainInfo['odpt:operator']),
                railway: removePrefix(trainInfo['odpt:railway']),
                status: trainInfo['odpt:trainInformationStatus'],
                text: trainInfo['odpt:trainInformationText']
            });
        }

        // Train information data from others
        for (const trainInfo of data.shift()) {
            trainInfoData.push(trainInfo);
        }

        return {trainData, trainInfoData};
    });
}

/**
 * Load the dynamic data for flights.
 * @returns {Object} Loaded data
 */
export function loadDynamicFlightData() {
    return Promise.all([
        configs.atisUrl,
        configs.flightUrl
    ].map(loadJSON)).then(data => ({
        atisData: data[0],
        flightData: data[1]
    }));
}

export function loadBusData(sources, lang) {
    const workerUrl = URL.createObjectURL(new Blob([`WORKER_STRING`], {type: 'text/javascript'})),
        worker = new Worker(workerUrl),
        proxy = Comlink.wrap(worker);

    return new Promise(resolve => proxy.load(sources, lang, Comlink.proxy(data => {
        const gtfsData = data.map((items, i) => ({
            featureCollection: geobuf.decode(new Pbf(items[0])),
            ...decode(new Pbf(items[1])),
            vehiclePositionUrl: sources[i].vehiclePositionUrl,
            color: sources[i].color
        }));

        proxy[Comlink.releaseProxy]();
        worker.terminate();
        resolve(gtfsData);
    })));
}

export function loadDynamicBusData(gtfsArray) {
    return Promise.all(gtfsArray.map(gtfs => fetch(gtfs.vehiclePositionUrl)
        .then(response => response.arrayBuffer())
        .then(data => ({
            gtfs,
            vehiclePosition: GtfsRealtimeBindings.transit_realtime.FeedMessage.decode(new Uint8Array(data))
        }))
    ));
}

export function updateOdptUrl(url, secrets) {
    if (url.startsWith('https://api.odpt.org/') && !url.match(/acl:consumerKey/)) {
        return `${url}${url.match(/\?/) ? '&' : '?'}acl:consumerKey=${secrets.odpt}`;
    } else if (url.startsWith('https://api-challenge2024.odpt.org/') && !url.match(/acl:consumerKey/)) {
        return `${url}${url.match(/\?/) ? '&' : '?'}acl:consumerKey=${secrets.challenge2024}`;
    }
    return url;
}

/**
 * Safieデバイス情報をAPI Gateway経由で取得する
 * @returns {Promise<Array>} Safieデバイス情報の配列
 */
export async function loadSafieDevices() {
    try {
        console.log('Safieデバイス情報を取得中...');
        
        const response = await fetch(configs.apiUrl.safie, {
            method: 'GET',
            headers: buildApiRequestHeaders({
                'Content-Type': 'application/json'
            })
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        console.log('Safieデバイス情報を取得しました:', data);

        // 現在時刻（JST）から1週間前の日時を計算
        const now = new Date();
        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

        // APIレスポンスを内部データ形式に変換（1週間以内に更新されたデバイスのみ）
        return data
            .filter(device => {
                if (!device.updatedAt) {
                    // updatedAtがない場合は表示しない
                    return false;
                }
                
                try {
                    // updatedAtはJST形式（例: 2025-08-29T09:04:48.953114+09:00）として取得される
                    const updatedAt = new Date(device.updatedAt);
                    
                    // 1週間以内に更新されているかチェック
                    return updatedAt >= oneWeekAgo;
                } catch (error) {
                    console.warn('updatedAtの解析に失敗しました:', device.updatedAt, error);
                    // 解析に失敗した場合は表示しない
                    return false;
                }
            })
            .map(device => ({
                id: device.deviceId,
                name: device.devicename,
                coord: [device.longitude, device.latitude],
                bearing: 0, // APIレスポンスにbearingがない場合はデフォルト値
                altitude: 10, // デフォルト高度
                latestsnapshot: device.latestsnapshot, // 画像データ
                updatedAt: device.updatedAt // 更新日時
            }));

    } catch (error) {
        console.error('Safieデバイス情報の取得に失敗しました:', error);
        return [];
    }
}

/**
 * Safieデバイス情報の定期更新を開始する
 * @param {Function} callback - デバイス情報が更新された際に呼び出されるコールバック関数
 * @param {number} intervalMs - 更新間隔（ミリ秒）デフォルトは3分
 * @returns {number} setIntervalのID（停止時に使用）
 */
export function startSafieDeviceUpdates(callback, intervalMs = 3 * 60 * 1000) {
    console.log(`Safieデバイス情報の定期更新を開始します（間隔: ${intervalMs / 1000}秒）`);
    
    // 初回実行
    loadSafieDevices().then(callback);
    
    // 定期実行
    return setInterval(async () => {
        const devices = await loadSafieDevices();
        callback(devices);
    }, intervalMs);
}

/**
 * Safieデバイス情報の定期更新を停止する
 * @param {number} intervalId - startSafieDeviceUpdatesで取得したID
 */
export function stopSafieDeviceUpdates(intervalId) {
    if (intervalId) {
        clearInterval(intervalId);
        console.log('Safieデバイス情報の定期更新を停止しました');
    }
}

/**
 * Ichimillデバイス情報をAPI Gateway経由で取得する
 * @returns {Promise<Array>} Ichimillデバイス情報の配列
 */
export async function loadIchimillDevices() {
    try {
        console.log('Ichimillデバイス情報を取得中...');
        
        const response = await fetch(configs.apiUrl.ichimill, {
            method: 'GET',
            headers: buildApiRequestHeaders({
                'Content-Type': 'application/json'
            })
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        console.log('Ichimillデバイス情報を取得しました:', data);

        // 現在時刻（JST）から1週間前の日時を計算
        const now = new Date();
        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

        // APIレスポンスを内部データ形式に変換（1週間以内に更新されたデバイスのみ）
        return data
            .filter(device => {
                if (!device.updatedAt) {
                    return false;
                }
                try {
                    const updatedAt = new Date(device.updatedAt);
                    return updatedAt >= oneWeekAgo;
                } catch (error) {
                    console.warn('updatedAtの解析に失敗しました:', device.updatedAt, error);
                    return false;
                }
            })
            .map(device => ({
                id: device.deviceId,
                name: device.devicename,
                coord: [device.longitude, device.latitude],
                bearing: 0,
                altitude: 10,
                updatedAt: device.updatedAt // 更新日時
            }));

    } catch (error) {
        console.error('Ichimillデバイス情報の取得に失敗しました:', error);
        return [];
    }
}

/**
 * Ichimillデバイス情報の定期更新を開始する
 * @param {Function} callback - デバイス情報が更新された際に呼び出されるコールバック関数
 * @param {number} intervalMs - 更新間隔（ミリ秒）デフォルトは3分
 * @returns {number} setIntervalのID（停止時に使用）
 */
export function startIchimillDeviceUpdates(callback, intervalMs = 3 * 60 * 1000) {
    console.log(`Ichimillデバイス情報の定期更新を開始します（間隔: ${intervalMs / 1000}秒）`);
    
    loadIchimillDevices().then(callback);
    
    return setInterval(async () => {
        const devices = await loadIchimillDevices();
        callback(devices);
    }, intervalMs);
}

/**
 * Ichimillデバイス情報の定期更新を停止する
 * @param {number} intervalId - startIchimillDeviceUpdatesで取得したID
 */
export function stopIchimillDeviceUpdates(intervalId) {
    if (intervalId) {
        clearInterval(intervalId);
        console.log('Ichimillデバイス情報の定期更新を停止しました');
    }
}

/**
 * 工事情報をAPI Gateway経由で取得する
 * @returns {Promise<Array>} 工事情報の配列
 */
export async function loadKoujiLocations() {
    try {
        console.log('工事情報を取得中...');

        const response = await fetch(configs.apiUrl.kouji, {
            method: 'GET',
            headers: buildApiRequestHeaders({
                'Content-Type': 'application/json'
            })
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const rawData = await response.json();
        let data = rawData;

        if (rawData && rawData.body !== undefined) {
            data = typeof rawData.body === 'string' ? JSON.parse(rawData.body) : rawData.body;
        }

        if (!Array.isArray(data)) {
            return [];
        }

        const normalizeCoord = (latitudeRaw, longitudeRaw) => {
            let latitude = Number(latitudeRaw),
                longitude = Number(longitudeRaw);

            if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
                return null;
            }

            // APIが緯度経度を逆順で返すケースを補正
            if (Math.abs(latitude) > 90 && Math.abs(longitude) <= 90) {
                [latitude, longitude] = [longitude, latitude];
            }

            if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) {
                return null;
            }

            return {latitude, longitude};
        };

        return data
            .map(record => {
                const coord = normalizeCoord(record.latitude, record.longitude);
                const statusRaw = (record.status || '').toString().toUpperCase();
                const status = statusRaw === 'NOTHING' ? 'UNDER_CONSTRUCTION' : statusRaw;

                if (!coord) {
                    return null;
                }

                return {record, latitude: coord.latitude, longitude: coord.longitude, status};
            })
            .filter(item => item && (item.status === 'UNDER_CONSTRUCTION' || item.status === 'COMPLETE'))
            .map(({record, latitude, longitude, status}) => ({
                id: record.koujiBangou || record.koujiName || `${longitude},${latitude}`,
                name: record.koujiName || record.koujiBangou || '工事情報',
                coord: [longitude, latitude],
                status,
                koujiBangou: record.koujiBangou || null,
                koujiName: record.koujiName || null,
                summary: record.gaiyou || null
            }));

    } catch (error) {
        console.error('工事情報の取得に失敗しました:', error);
        return [];
    }
}

/**
 * 工事情報の定期更新を開始する
 * @param {Function} callback - 工事情報が更新された際に呼び出されるコールバック関数
 * @param {number} intervalMs - 更新間隔（ミリ秒）デフォルトは3分
 * @returns {number} setIntervalのID（停止時に使用）
 */
export function startKoujiLocationUpdates(callback, intervalMs = 3 * 60 * 1000) {
    console.log(`工事情報の定期更新を開始します（間隔: ${intervalMs / 1000}秒）`);

    loadKoujiLocations().then(callback);

    return setInterval(async () => {
        const locations = await loadKoujiLocations();
        callback(locations);
    }, intervalMs);
}

/**
 * 工事情報の定期更新を停止する
 * @param {number} intervalId - startKoujiLocationUpdatesで取得したID
 */
export function stopKoujiLocationUpdates(intervalId) {
    if (intervalId) {
        clearInterval(intervalId);
        console.log('工事情報の定期更新を停止しました');
    }
}
